export type CounterType = 'CTU' | 'CTD' | 'CTUD';

export interface CounterState {
  type: CounterType;
  cv: number;
  pv: number;
  q: boolean;
  
  // Edge detection
  cuInput: boolean;
  cdInput: boolean;
}

export class CounterManager {
  private counters = new Map<string, CounterState>();

  public getCounter(id: string): CounterState | undefined {
    return this.counters.get(id);
  }

  public getOrCreateCounter(id: string, type: CounterType, pv: number): CounterState {
    let c = this.counters.get(id);
    if (!c) {
      c = {
        type,
        cv: type === 'CTD' ? pv : 0, // Initialization logic
        pv,
        q: false,
        cuInput: false,
        cdInput: false,
      };
      // Initial Q resolution
      this.resolveQ(c);
      this.counters.set(id, c);
    } else {
      // Update PV if it changed (e.g. edited and downloaded again)
      c.pv = pv;
    }
    return c;
  }

  public updateCU(id: string, input: boolean) {
    const c = this.counters.get(id);
    if (!c) return;

    if (input && !c.cuInput) {
      // Rising edge
      if (c.cv < 32767) {
        c.cv++;
      }
    }
    c.cuInput = input;
    this.resolveQ(c);
  }

  public updateCD(id: string, input: boolean) {
    const c = this.counters.get(id);
    if (!c) return;

    if (input && !c.cdInput) {
      // Rising edge
      if (c.cv > -32768) {
        c.cv--;
      }
    }
    c.cdInput = input;
    this.resolveQ(c);
  }

  public resetCounter(id: string) {
    const c = this.counters.get(id);
    if (!c) return;
    c.cv = 0;
    this.resolveQ(c);
  }

  public loadCounter(id: string) {
    const c = this.counters.get(id);
    if (!c) return;
    c.cv = c.pv;
    this.resolveQ(c);
  }

  private resolveQ(c: CounterState) {
    if (c.type === 'CTU' || c.type === 'CTUD') {
      c.q = c.cv >= c.pv;
    } else {
      c.q = c.cv <= 0;
    }
  }

  public getSnapshot(): Record<string, { type: CounterType; cv: number; pv: number; q: boolean }> {
    const snap: Record<string, { type: CounterType; cv: number; pv: number; q: boolean }> = {};
    for (const [id, c] of this.counters.entries()) {
      snap[id] = { type: c.type, cv: c.cv, pv: c.pv, q: c.q };
    }
    return snap;
  }

  public resetAll() {
    for (const c of this.counters.values()) {
      c.cv = c.type === 'CTD' ? c.pv : 0;
      c.q = false;
      c.cuInput = false;
      c.cdInput = false;
      this.resolveQ(c);
    }
  }
}
